import React, { createContext, useCallback, useContext, useReducer } from 'react';

const FilterContext = createContext();

const initialState = {
    selectedSystem: null,
    selectedFile: "all",
    tags: [],
    viewMode: 'circles',
    highlightedSystems: [],
    highlightedRecommendations: [],
};

function filterReducer(state, action) {
    switch (action.type) {
        case 'SELECT_SYSTEM': {
            const isDeselecting = state.selectedSystem === action.systemId || action.systemId === null;
            return {
                ...state,
                selectedSystem: isDeselecting ? null : action.systemId,
            };
        }

        case 'FORCE_SELECT_SYSTEM':
            return {
                ...state,
                selectedSystem: action.systemId,
            };

        case 'SELECT_SYSTEM_AND_VIEW':
            return {
                ...state,
                selectedSystem: action.systemId,
                viewMode: 'lineplot',
            };

        case 'SELECT_FILE':
            return { ...state, selectedFile: action.fileIndex };

        case 'SET_TAGS':
            return { ...state, tags: action.tags };

        case 'CYCLE_VIEW': {
            const next = state.viewMode === 'circles' ? 'lineplot'
                       : state.viewMode === 'lineplot' ? 'heatmap'
                       : 'circles';
            return { ...state, viewMode: next };
        }

        case 'SET_VIEW_MODE':
            return { ...state, viewMode: action.mode };

        case 'SET_HIGHLIGHTED_SYSTEMS':
            return { ...state, highlightedSystems: action.systems };

        case 'SET_HIGHLIGHTED_RECOMMENDATIONS':
            return { ...state, highlightedRecommendations: action.recs };

        case 'CLEAR_FILTERS':
            return {
                ...state,
                selectedSystem: null,
                tags: [],
                highlightedRecommendations: [],
            };

        case 'CLEAR_ALL':
            return { ...initialState };

        default:
            return state;
    }
}

export function FilterProvider({ children }) {
    const [state, dispatch] = useReducer(filterReducer, initialState);

    const selectSystem = useCallback((systemId) => {
        dispatch({ type: 'SELECT_SYSTEM', systemId });
    }, []);

    const forceSelectSystem = useCallback((systemId) => {
        dispatch({ type: 'FORCE_SELECT_SYSTEM', systemId });
    }, []);

    const selectSystemAndOpenLineplot = useCallback((systemId) => {
        dispatch({ type: 'SELECT_SYSTEM_AND_VIEW', systemId });
    }, []);

    const selectFile = useCallback((fileIndex) => {
        dispatch({ type: 'SELECT_FILE', fileIndex });
    }, []);

    const setTags = useCallback((tags) => {
        dispatch({ type: 'SET_TAGS', tags });
    }, []);

    const cycleView = useCallback(() => {
        dispatch({ type: 'CYCLE_VIEW' });
    }, []);

    const setViewMode = useCallback((mode) => {
        dispatch({ type: 'SET_VIEW_MODE', mode });
    }, []);

    const setHighlightedSystems = useCallback((systems) => {
        dispatch({ type: 'SET_HIGHLIGHTED_SYSTEMS', systems });
    }, []);

    const setHighlightedRecommendations = useCallback((recs) => {
        dispatch({ type: 'SET_HIGHLIGHTED_RECOMMENDATIONS', recs });
    }, []);

    const clearFilters = useCallback(() => {
        dispatch({ type: 'CLEAR_FILTERS' });
    }, []);

    const clearAll = useCallback(() => {
        dispatch({ type: 'CLEAR_ALL' });
    }, []);

    const value = {
        ...state,
        selectSystem,
        forceSelectSystem,
        selectSystemAndOpenLineplot,
        selectFile,
        setTags,
        cycleView,
        setViewMode,
        setHighlightedSystems,
        setHighlightedRecommendations,
        clearFilters,
        clearAll,
    };

    return (
        <FilterContext.Provider value={value}>
            {children}
        </FilterContext.Provider>
    );
}

export const useFilters = () => useContext(FilterContext);
